import React, { useEffect, useRef } from 'react';
import { Pagination } from 'components/molecules/Pagination';
import {
  AncillariesReferenceTableColumns,
  mapAncillariesReferenceToDataSource,
} from 'pages/AncillariesPage/helpers';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useNavigate } from 'react-router-dom';
import { CardList } from 'components/molecules/CardList';
import { Loader } from 'components/atoms/Loader';
import { EPerPages } from 'models/table';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import { AncillariesReferenceAccesoriesCard } from '../AncillariesReferenceAccesoriessCard';

interface IAncillariesReferenceAccesoriess {
  setAncillariesReferenceSort: (arg: any) => void;
  perPageAncillariesReference: EPerPages;
  setPerPageAncillariesReference: (arg: any) => void;
  currentPageAncillariesReference: number;
  setCurrentPageAncillariesReference: (arg: any) => void;
  isAncillariesReferenceLoading: boolean | null;
  paginatorAncillariesReference: any;
  ancillariesReference: any[];
  isTransactionPage?: boolean;
  LEId?: string;
  type?: string | null;
  isPaginationLoading: boolean;
  isQueryAncillariesReferenceLoading: boolean;
  isSortLoaded: boolean;
  isPaginationVisible: boolean;
}

const AncillariesReferenceAccesoriess = ({
  setAncillariesReferenceSort,
  perPageAncillariesReference,
  setPerPageAncillariesReference,
  currentPageAncillariesReference,
  setCurrentPageAncillariesReference,
  isAncillariesReferenceLoading,
  paginatorAncillariesReference,
  ancillariesReference,
  isTransactionPage = false,
  LEId,
  type,
  isPaginationLoading,
  isQueryAncillariesReferenceLoading,
  isSortLoaded,
  isPaginationVisible,
}: IAncillariesReferenceAccesoriess) => {
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();

  const scrollPosition = useRef(0);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  return (
    <div>
      {isAncillariesReferenceLoading || isQueryAncillariesReferenceLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={AncillariesReferenceTableColumns({
                isTransactionPage,
                handleViewTransaction: (id: string) =>
                  navigate(
                    `${ERouteLinks.LegalEntityViewAncillary.replace(':leId', LEId ?? '')
                      .replace(':id', id)
                      .replace(
                        ':type',
                        EArtAncillaryTypeName.ACCESSORY.toLowerCase(),
                      )}?type=${type}&expand=ancillaries`,
                  ),
                handleEdit: (id: string) =>
                  navigate(
                    ERouteLinks.EditAncillaries.replace(':id', id).replace(
                      ':type',
                      EArtAncillaryTypeName.ACCESSORY.toLowerCase(),
                    ),
                  ),
                handleSelect: (ancillary: any) =>
                  navigate(
                    `${ERouteLinks.LegalEntityAddAncillary.replace(
                      ':id',
                      LEId ?? '',
                    )}?type=${type}&expand=ancillaries`,
                    {
                      state: {
                        ancillary,
                        prevRoute: 'prevRoute',
                      },
                    },
                  ),
                LEId,
                type,
              })}
              dataSource={mapAncillariesReferenceToDataSource(
                ancillariesReference,
                EArtAncillaryTypeName.ACCESSORY,
              )}
              onSort={(it) => {
                setAncillariesReferenceSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              customPaddingIfNoData="30px"
            />
          ) : (
            <CardList
              items={mapAncillariesReferenceToDataSource(
                ancillariesReference,
                EArtAncillaryTypeName.ACCESSORY,
              )}
              render={(data, index) => (
                <AncillariesReferenceAccesoriesCard
                  data={data}
                  key={index}
                  handleView={(id: string) =>
                    navigate(
                      ERouteLinks.ViewAncillaries.replace(':id', id).replace(
                        ':type',
                        EArtAncillaryTypeName.ACCESSORY.toLowerCase(),
                      ),
                    )
                  }
                  handleEdit={(id: string) =>
                    navigate(
                      ERouteLinks.EditAncillaries.replace(':id', id).replace(
                        ':type',
                        EArtAncillaryTypeName.ACCESSORY.toLowerCase(),
                      ),
                    )
                  }
                  handleSelect={() => {
                    if (LEId)
                      navigate(
                        `${ERouteLinks.LegalEntityAddAncillary.replace(
                          ':id',
                          LEId,
                        )}?type=${type}&expand=ancillaries`,
                        {
                          state: {
                            ancillary: data,
                            prevRoute: 'prevRoute',
                          },
                        },
                      );
                  }}
                  isTransactionPage={isTransactionPage}
                />
              )}
            />
          )}

          {paginatorAncillariesReference && (
            <Pagination
              count={paginatorAncillariesReference?.totalElements}
              perPage={perPageAncillariesReference}
              onChangePage={(page) => {
                setCurrentPageAncillariesReference(page);
              }}
              onChangePerPage={(value) => {
                setPerPageAncillariesReference(value);
              }}
              current={currentPageAncillariesReference}
              isLoading={isPaginationLoading}
              isVisible={isPaginationVisible && isSortLoaded}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AncillariesReferenceAccesoriess;
